import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { DOC_TYPE_LABELS, docs, pageImageUrl, pageThumbUrl, type DocumentView } from "../lib/api";
import { ApiError } from "../lib/http";
import { documentsPath } from "./documentsFlow";
import { TableRowsPanel } from "../components/TableRowsPanel";
import { cellFrom, kindLabel, locationNote, pageFrom, rowsOn, sourceLabel, sweepNote, tagsOn } from "./documentViewFlow";
import { displayName } from "../lib/words";
import "../theme/docview.css";

/**
 * One document: its pages, its index metadata, and every tag the content sweep read in it, drawn
 * where it was read. Page and tag are in the address, so a reload or a shared link opens the same
 * place. Every box is a button: Tab reaches it, and hover or focus names the tag and its kind.
 * Tag text is only ever rendered as text.
 */
export function DocumentViewScreen() {
  const { package: pkg = "", documentId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const [doc, setDoc] = useState<DocumentView | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  // The page whose image has loaded. Boxes are drawn only over their own page, never over the
  // previous page's image while the next one is still on its way.
  const [loadedPage, setLoadedPage] = useState<number | null>(null);
  const boxes = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setDoc(null); setError(null);
    docs.document(pkg, documentId)
      .then((d) => { if (live) setDoc(d); })
      .catch((e) => { if (live) setError({ status: e instanceof ApiError ? e.status : 0, message: (e as Error).message }); });
    return () => { live = false; };
  }, [pkg, documentId]);

  const page = pageFrom(params.get("page"), doc?.page_count ?? 0);
  const selected = params.get("tag");
  const loaded = loadedPage === page;

  // A tag chosen from the list: move focus to its first box on the page once it is drawn.
  useEffect(() => {
    if (!selected || !loaded) return;
    boxes.current?.querySelector<HTMLButtonElement>(`[data-tag-box="${CSS.escape(selected)}"]`)?.focus();
  }, [selected, loaded, page]);

  // A value chosen from the rows panel: bring its mark on the page into view once drawn.
  const cell = params.get("cell");
  useEffect(() => {
    if (!cell || !loaded) return;
    const mark = boxes.current?.querySelector<HTMLElement>(".cell-box");
    if (mark && typeof mark.scrollIntoView === "function") {
      const smooth = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      mark.scrollIntoView({ block: "center", behavior: smooth ? "smooth" : "auto" });
    }
  }, [cell, loaded, page]);

  function go(next: number, tag: string | null = selected) {
    const q: Record<string, string> = { page: String(next) };
    if (tag) q.tag = tag;
    setParams(q);
  }

  function showCell(cell: string | null) {
    const q: Record<string, string> = { page: String(page) };
    if (cell) q.cell = cell;
    setParams(q);
  }

  const back = <Link to={documentsPath(pkg)}>{pkg} documents</Link>;
  if (error) {
    return (
      <div className="page">
        <h2 style={{ fontSize: 26, marginBottom: 6 }}>{error.status === 404 ? "No document at this address" : "This document could not be opened"}</h2>
        <p className="muted">{error.status === 404 ? "The link may be old or mistyped." : error.message} Back to {back}.</p>
      </div>
    );
  }
  if (!doc) {
    return <div className="page"><Loader2 className="muted spin" /> <span className="muted">Opening the document...</span></div>;
  }

  return (
    <DocumentBody doc={doc} page={page} selected={selected} cell={cell} loaded={loaded} back={back}
                  boxes={boxes} onGo={go} onCell={showCell} onLoaded={() => setLoadedPage(page)} />
  );
}

/** The document with its page, boxes and tag list. Pure, so it renders the same on the server. */
export function DocumentBody({ doc, page, selected, cell = null, loaded, back, boxes, onGo, onCell, onLoaded }: {
  doc: DocumentView; page: number; selected: string | null; cell?: string | null; loaded: boolean; back: React.ReactNode;
  boxes?: React.Ref<HTMLDivElement>; onGo: (page: number, tag?: string | null) => void;
  onCell?: (cell: string | null) => void; onLoaded?: () => void;
}) {
  const rows = rowsOn(doc, page);
  const shown = cellFrom(cell, doc, page);
  const onPage = tagsOn(doc, page);
  const notes = onPage.map(locationNote).filter((n): n is string => n !== null);
  const tagCount = (n: number) => tagsOn(doc, n).length;
  const sweep = sweepNote(doc.sweep, doc.tags.length > 0);

  return (
    <div className="page docview">
      <p className="muted" style={{ fontSize: 13, marginBottom: 6 }}>{back}</p>
      <h2 style={{ fontSize: 24, marginBottom: 4, overflowWrap: "anywhere" }} className="mono">{displayName(doc.file_name)}</h2>
      {doc.folder && <p className="muted" style={{ fontSize: 13, marginBottom: 16, overflowWrap: "anywhere" }}>{displayName(doc.folder)}</p>}

      <dl className="card docview-meta" aria-label="Index metadata">
        <dt>Title</dt><dd>{doc.title != null ? displayName(doc.title) : <span className="muted">not in index</span>}</dd>
        <dt>Type</dt><dd>{DOC_TYPE_LABELS[doc.doc_type ?? "unclassified"] ?? (doc.doc_type ?? "").replace(/_/g, " ")}</dd>
        <dt>Found via</dt><dd>{doc.found_via}</dd>
        {doc.databook && (<><dt>Databook</dt><dd>{doc.databook}</dd></>)}
        {doc.vendor && (<><dt>Vendor</dt><dd>{doc.vendor}</dd></>)}
        <dt>Pages</dt><dd>{doc.page_count.toLocaleString("en-US")}</dd>
        <dt>Approved</dt><dd>{doc.approved ? "yes" : "no"}</dd>
      </dl>
      {sweep && <p className="muted" style={{ fontSize: 13, margin: "12px 0" }}>{sweep}</p>}

      <div className="docview-grid">
        <nav className="docview-thumbs" aria-label="Pages">
          {Array.from({ length: doc.page_count }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button" className="ghost docview-thumb" aria-current={n === page ? "page" : undefined}
                    aria-label={`Page ${n}${tagCount(n) ? `, ${tagCount(n)} tag${tagCount(n) === 1 ? "" : "s"}` : ""}`}
                    onClick={() => onGo(n, null)}>
              <img src={pageThumbUrl(doc.path, n)} alt="" loading="lazy" />
              <span>{n}{tagCount(n) > 0 && <span className="pill info" style={{ marginLeft: 4 }}>{tagCount(n)}</span>}</span>
            </button>
          ))}
        </nav>

        <section aria-label={`Page ${page} of ${doc.page_count}`}>
          <div className="row docview-pager">
            <button type="button" className="ghost" aria-label="Previous page" disabled={page <= 1} onClick={() => onGo(page - 1, null)}>
              <ChevronLeft size={16} aria-hidden="true" />
            </button>
            <span aria-live="polite">Page {page} of {doc.page_count}</span>
            <button type="button" className="ghost" aria-label="Next page" disabled={page >= doc.page_count} onClick={() => onGo(page + 1, null)}>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          {notes.length > 0 && (
            <ul className="docview-notes" aria-label="Tags not marked on this page">
              {notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
          <div className="page-wrap docview-page" ref={boxes}>
            <img key={page} src={pageImageUrl(doc.path, page)} alt={`${displayName(doc.file_name)}, page ${page}`} onLoad={onLoaded}
                 style={{ opacity: loaded ? 1 : 0.4 }} />
            {loaded && onPage.flatMap((t) => t.boxes.map((b, i) => (
              <button key={`${t.tag}-${i}`} type="button" className="tag-box" data-tag-box={t.tag}
                      aria-pressed={selected === t.tag}
                      aria-label={`${t.tag}, ${kindLabel(t.kind)}${t.in_index === false ? ", not in index" : ""}`}
                      onClick={() => onGo(page, t.tag)}
                      style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.width * 100}%`, height: `${b.height * 100}%` }}>
                <span className="tag-box-tip" aria-hidden="true">
                  {t.tag} <span>{kindLabel(t.kind)}{t.in_index === false ? ", not in index" : ""}</span>
                </span>
              </button>
            )))}
            {loaded && shown?.box && (
              <div className="citation cell-box" aria-hidden="true" style={{
                left: `${shown.box.x * 100}%`, top: `${shown.box.y * 100}%`,
                width: `${shown.box.width * 100}%`, height: `${shown.box.height * 100}%`,
              }} />
            )}
          </div>
          {rows.length > 0 && <TableRowsPanel rows={rows} cell={cell} onCell={onCell ?? (() => undefined)} />}
        </section>

        <aside className="card docview-tags" aria-label="Tags in this document">
          <h3 style={{ fontSize: 15, marginBottom: 8 }}>Tags in this document</h3>
          {doc.tags.length === 0 && <p className="muted" style={{ fontSize: 13 }}>The content sweep read no tag in this document.</p>}
          <ul>
            {doc.tags.map((t) => (
              <li key={t.tag} data-tag={t.tag} className={selected === t.tag ? "active" : undefined}>
                <span className="mono">{t.tag}</span> <span className="muted">{kindLabel(t.kind)}</span>
                {t.in_index === false && <span className="pill review docview-not-indexed">not in index</span>}
                {t.sources && t.sources.length > 0 && (
                  <span className="muted docview-tag-sources">{t.sources.map(sourceLabel).join(", ")}</span>
                )}
                <span className="docview-tag-pages">
                  {t.pages.map((n) => (
                    <button key={n} type="button" className="ghost" aria-label={`${t.tag} on page ${n}`}
                            aria-current={selected === t.tag && page === n ? "location" : undefined}
                            onClick={() => onGo(n, t.tag)}>
                      p. {n}
                    </button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  );
}
